"use client";

import * as React from "react";

/**
 * P11-19 · One picker, many triggers.
 *
 * The selector started as a single component that owned both its trigger and
 * its dialog. That works for exactly one entry point. P11-DB01 asks for three —
 * header, mobile menu, footer — and the naive version breaks on the second:
 *
 *  - **Nested focus traps.** The mobile menu is itself a modal dialog. A picker
 *    rendered inside it puts a trap inside a trap, and Escape becomes ambiguous.
 *  - **Unmount races.** A trigger inside the drawer that closes the drawer
 *    would unmount the very dialog it just opened.
 *  - **Duplicate test ids and duplicate state**, three copies of a draft that
 *    can disagree.
 *
 * So the dialog is rendered ONCE at the layout level and the pills are only
 * triggers. Opening from the mobile menu closes the drawer and leaves the
 * picker — which lives outside it — standing.
 */

interface MarketPickerState {
  open: boolean;
  /** Opens the picker. Triggers inside a modal pass their own dismiss here. */
  openPicker: (beforeOpen?: () => void) => void;
  setOpen: (open: boolean) => void;
  /** Focus returns here on close — the trigger that opened it. */
  lastTrigger: React.MutableRefObject<HTMLElement | null>;
}

const MarketPickerContext = React.createContext<MarketPickerState | null>(null);

export function MarketPickerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const lastTrigger = React.useRef<HTMLElement | null>(null);

  const openPicker = React.useCallback((beforeOpen?: () => void) => {
    // Remember who opened it, so closing can hand focus back to that trigger
    // rather than dumping the shopper at the top of the document.
    lastTrigger.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    beforeOpen?.();
    setOpen(true);
  }, []);

  const value = React.useMemo(
    () => ({ open, openPicker, setOpen, lastTrigger }),
    [open, openPicker],
  );

  return (
    <MarketPickerContext.Provider value={value}>
      {children}
    </MarketPickerContext.Provider>
  );
}

/**
 * Null outside the provider rather than throwing: the pill renders in the
 * header, which is also used by surfaces that do not mount the picker. A
 * trigger with nothing to open should render nothing, not crash the page.
 */
export function useMarketPicker(): MarketPickerState | null {
  return React.useContext(MarketPickerContext);
}
